import { useState, useMemo } from 'react';
import { isMobile } from 'react-device-detect';

import { Drawer, DrawerContent, TextArea, Button, Icon } from '@pella/sash-framework';
import { useAddCommunication } from '../../../../hooks';
import { convertISO } from '../../../../utils';
import { MAX_COMMUNICATION_LENGTH } from '../../../../data/constants';

import * as classes from './communication.module.css';

/**
 * Orders a communication history newest-first. The stored array is append-ordered, so
 * entries sharing a timestamp fall back to reverse insertion order.
 * @param {Local.Communication[]} history - The stored communication history.
 * @returns {Local.Communication[]} The history ordered newest to oldest.
 */
const orderNewestFirst = (history) =>
  history
    .map((entry, index) => ({ entry, index }))
    .toSorted((a, b) => {
      const byTimestamp = new Date(b.entry.timeStamp).getTime() - new Date(a.entry.timeStamp).getTime();
      return byTimestamp || b.index - a.index;
    })
    .map(({ entry }) => entry);

/**
 * Component for the Communication Drawer. Shows the request's action details alongside
 * its communication history, and lets the user append a new communication.
 * @param {Local.CommunicationDrawerProps} props - Component props
 */
export const CommunicationDrawer = ({ open, close, request }) => {
  const [draft, setDraft] = useState('');
  const { mutate: addCommunication, isPending, data: savedHistory } = useAddCommunication();

  const communications = useMemo(() => {
    const stored = request.communications ?? [];

    // The mutation returns the full saved history, so a just-saved entry shows immediately
    // without waiting for the parent's cached request to flow back down.
    return orderNewestFirst(savedHistory ?? stored);
  }, [request, savedHistory]);

  /** Closes the drawer, discarding any unsaved input. */
  const handleClose = () => {
    setDraft('');
    close();
  };

  /** Saves the draft as a new communication and clears the input. */
  const handleSave = () => {
    addCommunication({ request, text: draft }, { onSuccess: () => setDraft('') });
  };

  return (
    <Drawer direction={isMobile ? 'bottom' : 'right'} open={open} onOpenChange={handleClose}>
      <DrawerContent className={classes.drawer} data-testid='communication-drawer' showClose={false}>
        <div className={classes.content}>
          <div className={classes.header}>
            <div className={classes.action}>
              <b>{request.action.description}</b>
              <div>{request.action.message}</div>
            </div>
            <button type='button' className={classes.close} aria-label='Close' onClick={handleClose}>
              <Icon name='close' />
            </button>
          </div>

          <div className={classes.requestor}>
            <div className={classes.info}>
              <Icon name='account_circle' size={16} weight={700} />
              <div data-testid='communication-requestor'>{request.requestorId}</div>
            </div>
            <div className={classes.info}>
              <Icon name='schedule' size={16} weight={700} />
              <div data-testid='communication-created'>{convertISO(request.creationDate)}</div>
            </div>
          </div>

          <div className={classes.divider} />

          <div className={classes.inputWrapper}>
            <div className={classes.charCount} data-testid='communication-char-count'>
              {draft.length} / {MAX_COMMUNICATION_LENGTH}
            </div>
            <TextArea
              id='communication-input'
              data-testid='communication-input'
              labelText='Communication'
              placeholder='Add a communication here'
              rows={3}
              value={draft}
              maxLength={MAX_COMMUNICATION_LENGTH}
              onChange={(/** @type {React.ChangeEvent<HTMLTextAreaElement>} */ e) => setDraft(e.target.value)}
            />
          </div>

          <div className={classes.drawerFooter}>
            <Button
              data-testid='saveCommunication'
              className={classes.button}
              color='primary'
              onClick={handleSave}
              disabled={!draft.trim() || isPending}
            >
              SAVE
            </Button>
          </div>

          <div className={classes.history} data-testid='communication-history'>
            {communications.map((entry) => (
              <div
                // Entries carry no server-side id, so the submitter, time, and text form the key.
                key={`${entry.timeStamp}-${entry.userName}-${entry.communication}`}
                className={classes.entry}
                data-testid='communication-entry'
              >
                <div className={classes.entryText}>{entry.communication}</div>
                <div className={classes.info}>
                  <Icon name='account_circle' size={16} weight={700} />
                  <div className={classes.user}>{entry.userName}</div>
                </div>
                <div className={classes.info}>
                  <Icon name='schedule' size={16} weight={700} />
                  <div>{convertISO(entry.timeStamp)}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  );
};
